import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link, useParams } from "react-router-dom";
import { dossierService } from "../lib/dossierService";
import { buildDossierPath, DOSSIER_MODES, isExpressDossierMode } from "../data/dossierModes";
import { getDossierCatalog } from "../data/dossierCatalog";
import { assetURL } from "../components/dossier/PDFPreview";

/** Portada genérica */
const COVER_SESSIONS = "/images/dossier/cover-sessions.png";

/** Miniatura con fallback: intenta PNG, luego JPG y finalmente portada genérica */
function SessionThumb({ id, title }) {
  const [src, setSrc] = useState(`/images/dossier/sessions/${id}.png`);
  const [tried, setTried] = useState({ png: true, jpg: false, cover: false });

  const onError = () => {
    if (tried.png && !tried.jpg) {
      setSrc(`/images/dossier/sessions/${id}.jpg`);
      setTried((s) => ({ ...s, jpg: true }));
    } else if (!tried.cover) {
      setSrc(COVER_SESSIONS);
      setTried((s) => ({ ...s, cover: true }));
    }
  };

  return (
    <img
      src={src}
      alt={title ? `Sessió ${id}: ${title}` : `Sessió ${id}`}
      loading="lazy"
      decoding="async"
      className="w-full h-full object-cover hover:scale-[1.02] transition-transform duration-700"
      onError={onError}
    />
  );
}

export default function SessionsIndexPage({ mode = DOSSIER_MODES.STANDARD }) {
  const { t, i18n } = useTranslation();
  const { blocSlug } = useParams();

  const isExpress = isExpressDossierMode(mode);
  const materialLocale = i18n.resolvedLanguage || i18n.language;
  const catalog = getDossierCatalog(mode, materialLocale);
  const sessionsObj = t("dossier_didactic.sessions", {
    returnObjects: true,
    defaultValue: {},
  });

  const sessions = useMemo(() => {
    if (isExpress) {
      return (catalog.sessions || []).map((session) => [
        session.id,
        { guia: session.title, resources: session.resources || [] },
      ]);
    }
    return Object.entries(sessionsObj)
      .filter(([k]) => !Number.isNaN(Number(k)))
      .sort((a, b) => Number(a[0]) - Number(b[0]));
  }, [catalog.sessions, isExpress, sessionsObj]);

  const [bySessionDoc, setBySessionDoc] = useState({}); // { "7": {url, name}, ... }

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const docs = await dossierService.listDocuments?.(mode, materialLocale);
        const map = {};
        (docs || []).forEach((d) => {
          if (d.sessionId && (d.ext === "docx" || isExpress)) {
            if (!map[d.sessionId]) map[d.sessionId] = { url: d.url, name: d.name };
          }
        });
        if (mounted) setBySessionDoc(map);
      } catch {
        if (mounted) setBySessionDoc({});
      }
    })();
    return () => { mounted = false; };
  }, [isExpress, materialLocale, mode]);

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold">
          {t("ui.dossier.sessions.title", "Sessions")}
        </h2>
        <p className="text-sm text-gray-600">
          {blocSlug
            ? t("ui.dossier.sessions.subtitle", "Selecció de sessions del 1 al 25")
            : t("ui.dossier.sessions.subtitle", "Selecció de sessions del 1 al 25")}
        </p>
      </div>

      <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-5">
        {sessions.map(([id, s]) => {
          const doc = bySessionDoc[id] || s?.resources?.find((resource) => resource.resourceKey === "mainSessionMaterial") || null;
          const title = s?.guia || `${t("ui.dossier.session", "Sessió")} ${id}`;
          return (
            <article
              key={id}
              className="rounded-2xl border bg-white overflow-hidden hover:shadow-md transition flex flex-col"
            >
              <div className="aspect-[3/2] bg-gray-100 overflow-hidden">
                <SessionThumb id={id} title={title} />
              </div>

              <div className="p-5 flex-1 flex flex-col">
                <h3 className="font-semibold text-[15px]">{title}</h3>

                {(s?.descripcio || s?.description) && (
                  <p className="text-sm mt-2 line-clamp-4">
                    {s?.descripcio || s?.description}
                  </p>
                )}

                <div className="mt-auto pt-4 flex gap-2 flex-wrap">
                  <Link
                    to={buildDossierPath(`/dossier/sessions/${id}`, mode)}
                    className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl bg-white hover:bg-gray-50 transition"
                  >
                    {t("ui.common.viewDetails", "Veure detall")}
                  </Link>

                  {(doc?.url || doc?.path) && (
                    <a
                      href={doc.url || assetURL(doc.path)}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 text-sm font-medium px-3 py-2 border rounded-xl bg-emerald-600 text-white hover:bg-emerald-700 transition"
                      download
                    >
                      {t("dossier_didactic.download", "Descarregar")}
                    </a>
                  )}
                </div>
              </div>
            </article>
          );
        })}
      </div>

      <div className="pt-2">
        <Link to={buildDossierPath("/dossier", mode)} className="underline text-sm">
          ← {t("ui.dossier.links.backOverview", "Tornar a la vista general")}
        </Link>
      </div>
    </div>
  );
}
